import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AddressModel, CarrierModel, CustomHttpErrorResponse, Error } from '../../../../../../common';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { ActiveCompartmentsModel, UliCreationResponse, UliRule } from '../models/uli-creation.model';
import {
  MultiStopUliCreate,
  SetUliRule,
  UliClearLoadSearch,
  UliCreate,
  UliCreateAndAllocate,
  UliCreationCarriersLoad,
  UliCreationClearStorage,
  UliCreationCompartmentListLoad,
  UliCreationDefaultHubTruckMappingLoad,
  UliCreationDeliveryPartyAddressesLoad,
  UliCreationHubTruckMappingLoad,
  UliCreationInitialState,
  UliCreationSetCalendarPrefill,
  UliCreationSetPrefill,
  UliLoadSearch,
} from './uli-creation.actions';
import { UliCreationService } from './uli-creation.service';

import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NavigateUserService } from '../../../../../services/navigate-user.service';
import { AllocationViewSetLastChangedUli } from '../../allocation-view.actions';
import {
  AllocationResponseModel,
  CalendarBasedUliCreationFormModel,
  CreateAndAllocateUliRequestModel,
  SlotCompactInfo,
  UliCreateSelectionModel,
} from '../../models';
import { ServerMessagesFacade, TopicsEnum, UliCreateServerMessageModel } from '../../server-messages';
import { UliLoadSearchModel } from '../models';
import { UliDefaultHubTruckMappingModel } from '../models/uli-default-hub-truck-mapping.model';
import { HubTrucksModel } from '../../../../administration';

export interface UliCreationStateModel {
  compartments: ActiveCompartmentsModel[];
  deliveryPartyAddresses: AddressModel[];
  canLoadMoreDeliveryPartyAddress: boolean;
  carriers: CarrierModel[];
  canLoadMoreCarriers: boolean;
  loadSearchResult: UliLoadSearchModel;
  defaultHubTruckMapping: UliDefaultHubTruckMappingModel;
  hubTruckMapping: HubTrucksModel;
  ruleCreation: {
    uliRule: UliRule;
  };
  autoAllocation: {
    response: AllocationResponseModel;
  };
  prefillData: UliCreateSelectionModel | null;
  calendarPrefillData: CalendarBasedUliCreationFormModel;
}

const initialState: UliCreationStateModel = {
  compartments: [],
  deliveryPartyAddresses: [],
  canLoadMoreDeliveryPartyAddress: true,
  carriers: [],
  canLoadMoreCarriers: true,
  loadSearchResult: null,
  defaultHubTruckMapping: null,
  hubTruckMapping: null,
  ruleCreation: {
    uliRule: null,
  },
  autoAllocation: {
    response: null,
  },
  prefillData: null,
  calendarPrefillData: null,
};

export const pollingDelay = 1000;

@State<UliCreationStateModel>({
  name: 'UliCreation',
  defaults: initialState,
})
@Injectable()
export class UliCreationState {
  constructor(
    private uliCreationService: UliCreationService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
    private serverMessagesFacade: ServerMessagesFacade,
  ) {}

  @Selector()
  static getState(state: UliCreationStateModel): UliCreationStateModel {
    return state;
  }

  @Selector()
  static getCompartments(state: UliCreationStateModel): ActiveCompartmentsModel[] {
    return state.compartments;
  }

  @Selector()
  static getPartyDeliveryAddresses(state: UliCreationStateModel): AddressModel[] {
    return state.deliveryPartyAddresses;
  }

  @Selector()
  static isMoreDeliveryAddressesLoadable(state: UliCreationStateModel): boolean {
    return state.canLoadMoreDeliveryPartyAddress;
  }

  @Selector()
  static getCarriers(state: UliCreationStateModel): CarrierModel[] {
    return state.carriers;
  }

  @Selector()
  static isMoreCarriersLoadable(state: UliCreationStateModel): boolean {
    return state.canLoadMoreCarriers;
  }

  @Selector()
  static getHubTruckMapping(state: UliCreationStateModel): HubTrucksModel {
    return state.hubTruckMapping;
  }

  @Selector()
  static getDefaultHubTruckMapping(state: UliCreationStateModel): UliDefaultHubTruckMappingModel {
    return state.defaultHubTruckMapping;
  }

  @Selector()
  static getUliRule(state: UliCreationStateModel): UliRule {
    return state.ruleCreation.uliRule;
  }

  @Selector()
  static getSlot(state: UliCreationStateModel): SlotCompactInfo {
    return state.ruleCreation.uliRule.slot;
  }

  @Selector()
  static getLoadSearchResult(state: UliCreationStateModel): UliLoadSearchModel {
    return state.loadSearchResult;
  }

  @Selector()
  static getPrefillData(state: UliCreationStateModel): UliCreateSelectionModel {
    return state.prefillData;
  }

  @Selector()
  static getCalendarPrefillData(state: UliCreationStateModel): CalendarBasedUliCreationFormModel {
    return state.calendarPrefillData;
  }

  @Action(UliCreationHubTruckMappingLoad)
  getHubTruckMapping(
    ctx: StateContext<UliCreationStateModel>,
    { payload }: UliCreationHubTruckMappingLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliCreationService.getHubTruckMapping(payload).pipe(
      map((hubTruckMapping: HubTrucksModel) => {
        ctx.patchState({
          hubTruckMapping,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliCreationDefaultHubTruckMappingLoad)
  getDefaultHubTruckMapping(
    ctx: StateContext<UliCreationStateModel>,
    { payload }: UliCreationDefaultHubTruckMappingLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliCreationService.getDefaultHubTruckMapping(payload).pipe(
      map((defaultHubTruckMapping: UliDefaultHubTruckMappingModel) => {
        ctx.patchState({
          defaultHubTruckMapping,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliCreationCompartmentListLoad)
  getCompartmentList(
    ctx: StateContext<UliCreationStateModel>,
    { payload }: UliCreationCompartmentListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliCreationService.getCompartmentList(payload).pipe(
      map((response: ActiveCompartmentsModel[]) => {
        ctx.patchState({
          compartments: [...response],
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliCreationDeliveryPartyAddressesLoad)
  getDeliveryAddresses(
    ctx: StateContext<UliCreationStateModel>,
    { payload: { filter, limit } }: UliCreationDeliveryPartyAddressesLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliCreationService.getDeliveryAddressesList(filter, limit).pipe(
      map((addresses: AddressModel[]) => {
        ctx.patchState({
          deliveryPartyAddresses: [...addresses],
          canLoadMoreDeliveryPartyAddress: addresses.length >= limit,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliCreationCarriersLoad)
  getCarriers(
    ctx: StateContext<UliCreationStateModel>,
    { payload: { filter, limit } }: UliCreationCarriersLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliCreationService.getCarriers(filter, limit).pipe(
      map((carriers: CarrierModel[]) => {
        ctx.patchState({
          carriers: [...carriers],
          canLoadMoreCarriers: carriers.length >= limit,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliLoadSearch)
  loadSearch(
    ctx: StateContext<UliCreationStateModel>,
    { payload: { loadNumber } }: UliLoadSearch,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.uliCreationService.isLoadExisting(loadNumber).pipe(
      map((exist: boolean) => {
        ctx.patchState({ loadSearchResult: new UliLoadSearchModel({ loadNumber, exist }) });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Load);
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(SetUliRule)
  setUliRule(ctx: StateContext<UliCreationStateModel>, { payload }: SetUliRule): void {
    this.uliCreationService.saveUliRule(payload);
    ctx.patchState({ ruleCreation: { uliRule: payload } });
  }

  @Action(UliClearLoadSearch)
  clearLoadSearch(ctx: StateContext<UliCreationStateModel>): void {
    ctx.patchState({ loadSearchResult: null });
  }

  @Action(UliCreate)
  createUli(
    ctx: StateContext<UliCreationStateModel>,
    { payload }: UliCreate,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.uliCreationService.createUli(payload).pipe(
      map((lastCreatedUli: UliCreationResponse) => {
        ctx.dispatch(new AllocationViewSetLastChangedUli(lastCreatedUli.uli));

        this.navigateToAllocationView();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Load);
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Uli);
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(UliCreateAndAllocate)
  createAndAllocateUli(
    ctx: StateContext<UliCreationStateModel>,
    { payload }: UliCreateAndAllocate,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.uliCreationService.createAndAllocateUli(payload).pipe(
      switchMap((allocatedUli: CreateAndAllocateUliRequestModel) =>
        this.serverMessagesFacade.loadTopicMessages$.pipe(
          tap((messages: UliCreateServerMessageModel[]) => {
            this.handleServerMessages(messages, allocatedUli, ctx);
          }),
          map(() => {
            this.navigateToAllocationView();
          }),
        ),
      ),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Load);
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Uli);
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(MultiStopUliCreate)
  createMultiStopUli(
    ctx: StateContext<UliCreationStateModel>,
    { payload }: MultiStopUliCreate,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.uliCreationService.createMultiStopUli(payload).pipe(
      map((lastCreatedUli: UliCreationResponse) => {
        ctx.dispatch(new AllocationViewSetLastChangedUli(lastCreatedUli.uli));

        this.navigateToAllocationView();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Load);
        this.serverMessagesFacade.unsubscribeFromTopic(TopicsEnum.Uli);
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  private handleServerMessages(
    messages: UliCreateServerMessageModel[],
    allocatedUli: CreateAndAllocateUliRequestModel,
    ctx: StateContext<UliCreationStateModel>,
  ): void {
    const message = messages.length ? messages[messages.length - 1] : null;
    if (message?.details) {
      this.notificationService.error(`${message.message} - ${message.details}`);
      this.serverMessagesFacade.setInitialState();
    } else {
      ctx.dispatch(new AllocationViewSetLastChangedUli(allocatedUli.createUliView.uliInformation.uli));
    }
  }

  @Action(UliCreationSetPrefill)
  setPrefillData(ctx: StateContext<UliCreationStateModel>, { payload }: UliCreationSetPrefill): void {
    ctx.patchState({
      prefillData: payload,
    });
  }

  @Action(UliCreationSetCalendarPrefill)
  setCalendarPrefillData(ctx: StateContext<UliCreationStateModel>, { payload }: UliCreationSetCalendarPrefill): void {
    this.uliCreationService.saveUliCalendar(payload);

    ctx.patchState({
      calendarPrefillData: payload,
    });
  }

  @Action(UliCreationClearStorage)
  clearStorageData(_ctx: StateContext<UliCreationStateModel>): void {
    this.uliCreationService.clearStorage();
  }

  @Action(UliCreationInitialState)
  setInitialState(ctx: StateContext<UliCreationStateModel>): void {
    ctx.setState(initialState);
  }

  private navigateToAllocationView(): void {
    this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/allocation-view');
  }
}
